import {
  SupplyChainGenre,
  SupplyChainLanguage,
  SupplyChainParentalAdvisory,
  SupplyChainPrimaryGenre,
} from "../../api/supplyChain/supplyChainModels";
import { OptionType } from "../../stories/elements/DropDownSelector/DropdownSelector";
import EntityPhoto from "./entityPhoto";
import { ScheduledProject } from "./scheduledproject";
import { EP_SONG_COUNT } from "./service";
import { BareUser } from "./user";

export enum CreateReleaseFlow {
  FREE = "free",
  DTC = "dtc",
  DSP = "dsp",
}

export enum ReleaseType {
  NO_RELEASE_TYPE,
  SINGLE,
  EP,
  ALBUM,
}

/**
 * A frontend copy of the Django ReleaseProductType enum used in the
 * ReleaseGetDatabaseController._annotate_release_queryset() method
 */
export enum ReleaseProductType {
  RELEASE = "Release",
  DTC = "DTC",
  DSP = "DSP",
}

export const ReleaseTypeOptions: OptionType[] = [
  {
    value: ReleaseType.NO_RELEASE_TYPE,
    label: "All",
  },
  {
    value: ReleaseType.SINGLE,
    label: "Single",
  },
  {
    value: ReleaseType.EP,
    label: "EP",
  },
  {
    value: ReleaseType.ALBUM,
    label: "Album",
  },
];

export enum ReleaseStatus {
  SCHEDULED = "Scheduled",
  FOR_FREE = "For Free",
  NOT_LIVE = "Not Live",
  FOR_SALE = "For Sale",
  DRAFT = "Draft",
}
export interface Release {
  id: number;
  title: string;
  artist_user_id: number;
  artist_user: null | BareUser;
  artist_display_name: string | null;
  description: null | string;
  featured: null | string;
  has_issues: boolean;
  release_type: ReleaseType;
  release_cover: null | EntityPhoto;
  created_at: string;
  published_at: string | null;
  published_end: string | null;
  deleted_at: string | null;
  payment_required_for_playback: boolean;
  supply_chain_language: null | SupplyChainLanguage["id"];
  supply_chain_main_genre: null | SupplyChainPrimaryGenre["id"];
  supply_chain_secondary_genre: null | SupplyChainGenre["id"];
  parental_advisory: null | SupplyChainParentalAdvisory["id"];
  tracks_count: number | null;
  product_types: string[] | null;
  status: ReleaseStatus | null;
  supply_chain_product_id: number;
  supply_chain_file_id: number;
  duration: string | null;
}

export const getReleaseType = (numOfSongs: number) => {
  if (numOfSongs === 0) {
    return ReleaseType.NO_RELEASE_TYPE;
  }
  if (numOfSongs === 1) {
    return ReleaseType.SINGLE;
  }
  if (numOfSongs <= EP_SONG_COUNT) {
    return ReleaseType.EP;
  }
  return ReleaseType.ALBUM;
};

export const getProjectIdsFromScheduledProject = (
  scheduledProject: ScheduledProject | null | undefined,
) => {
  if (!scheduledProject) return [];

  const projectIdsMap: Record<number, boolean> = {};
  const projectIdsWithPrereq: Record<number, boolean> = {};

  // Get all project ids that are not prereq
  scheduledProject.projects.forEach((project) => {
    // If we already have this project stored as one that was the prereq of another project, skip it
    if (projectIdsWithPrereq[project.id]) return;
    if (project.prereq_project_id) {
      // If the prereq_project_id is one we already stored, delete it from our known map
      if (projectIdsMap[project.prereq_project_id]) {
        delete projectIdsMap[project.prereq_project_id];
      }
      projectIdsWithPrereq[project.prereq_project_id] = true;
    }
    projectIdsMap[project.id] = true;
  });

  return projectIdsMap;
};

export interface ReleaseTrack {
  id: number;
  audio_download_mp3_enabled: boolean;
  audio_download_wav_enabled: boolean;
  file_version_id: number | null;
  file_name: string | null;
  parental_advisory: number | null;
  release: Release;
  release_track_purchased_by_current_user: boolean;
  supply_chain_language: number | null;
  supply_chain_main_genre: number | null;
  supply_chain_secondary_genre: number | null;
  supply_chain_product_id: number | null;
  supply_chain_file_id: number | null;
  title: string;
  track_number: number;
  // Temporarily hide play count
  // play_count: number;
  recording_engineer_user_id: number | null;
  mixing_engineer_user_id: number | null;
  dolby_atmos_mixing_engineer_user_id: number | null;
  mastering_engineer_user_id: number | null;
  project_id: number | null;
  featured_artists: string | null;
}

export interface ReleaseAndReleaseTracks extends Release {
  releasetrack_set: ReleaseTrack[];
}

export interface FeaturedHomeReleaseTrack {
  id: number;
  dolby_atmos_mixing_engineer: null | BareUser;
  file_version_id: number | null;
  mastering_engineer: null | BareUser;
  mixing_engineer: null | BareUser;
  recording_engineer: null | BareUser;
  release: Release;
  title: string;
}

export const DEFAULT_RELEASE_TITLE = "Untitled Release";
